import { isWorldVersion } from "odyssey-shared";
import { gameReport } from "./report.ts";
import { probeStatus } from "./status-poll.ts";

/**
 * Which world version the game page loads, and when it reloads for another (ADR-050's amendment of 2026-10-02,
 * P1.4-T44). The page loads the version the gateway's `/status` says its map server runs, so a page opened between a
 * publish moving the CDN's pointer and the server's swap loads the world the server still runs instead of being
 * refused. A `welcome` that names another version (the server swapped between the probe and the join) or a
 * `WorldUpdated` close (4007, the server swapped while the page played) reloads the page, which asks again; a
 * `welcome`'s version is kept for the next load in case `/status` cannot be read (an older gateway, an origin it does
 * not admit). World reloads are bounded by a count a minute, so a server that keeps naming a version the page cannot
 * load ends at the refusal with Reload rather than a reload loop; the rules guard (ADR-069 decision 8) stays its own.
 */

/**
 * The window world reloads are counted in.
 */
export const WORLD_RELOAD_WINDOW_MS = 60_000;

/**
 * World reloads admitted in one window: a page caught by a publish reloads once for its join and once for the swap,
 * and a rollback straight after may cost one more.
 */
export const WORLD_RELOADS_PER_WINDOW = 3;

/**
 * How long the boot waits for `/status` before it reads the pointer instead.
 */
export const RUNNING_VERSION_TIMEOUT_MS = 3_000;

const RELOADS_KEY = "odyssey.world-reloads";
const VERSION_KEY = "odyssey.world-version";

/**
 * The store the page keeps its world reloads and its version hint in: the tab's `sessionStorage`.
 */
export type ReloadStore = Pick<Storage, "getItem" | "setItem" | "removeItem">;

/**
 * Whether one more world reload is admitted now, and the times to keep if it is.
 *
 * @param stored The stored reload times (a JSON array of milliseconds), or null when none are kept.
 * @param now The clock now, in milliseconds.
 * @returns `admitted`, the times within the window with now added when admitted, and the age of the oldest kept.
 * @example
 * admitWorldReload("[1000, 2000, 3000]", 4000); // { admitted: false, times: [1000, 2000, 3000], oldestMs: 3000 }
 */
export function admitWorldReload(stored: string | null, now: number): { readonly admitted: boolean; readonly times: readonly number[]; readonly oldestMs: number } {
  let parsed: unknown = [];
  try {
    parsed = stored === null ? [] : JSON.parse(stored);
  } catch {
    parsed = [];
  }
  const recent = (Array.isArray(parsed) ? parsed : []).filter((time): time is number => typeof time === "number" && Number.isFinite(time) && now - time < WORLD_RELOAD_WINDOW_MS && time <= now);
  const oldestMs = recent.length === 0 ? 0 : now - Math.min(...recent);
  if (recent.length >= WORLD_RELOADS_PER_WINDOW) {
    return { admitted: false, times: recent, oldestMs };
  }
  return { admitted: true, times: [...recent, now], oldestMs };
}

/**
 * Reloads the page for a world version it does not hold, unless the minute's count is spent: records the reload, and
 * the version to load next when the caller knows it.
 *
 * @param version The version the server named, kept for the next load, or null when the caller does not know it.
 * @param store The tab's store.
 * @param reload Reloads the page.
 * @param now The clock now, in milliseconds.
 * @returns `reloading`; or `held` with how long ago the oldest counted reload was when the count is spent; or
 *   `unavailable` when the store cannot be used, which reloads nothing, since nothing could bound the loop.
 */
export function reloadForWorldVersion(version: string | null, store: ReloadStore, reload: () => void, now: number): { readonly kind: "reloading" } | { readonly kind: "held"; readonly oldestMs: number } | { readonly kind: "unavailable" } {
  try {
    const decision = admitWorldReload(store.getItem(RELOADS_KEY), now);
    if (!decision.admitted) {
      return { kind: "held", oldestMs: decision.oldestMs };
    }
    store.setItem(RELOADS_KEY, JSON.stringify(decision.times));
    if (version !== null && isWorldVersion(version)) {
      store.setItem(VERSION_KEY, version);
    } else {
      store.removeItem(VERSION_KEY);
    }
  } catch {
    return { kind: "unavailable" };
  }
  reload();
  return { kind: "reloading" };
}

/**
 * Reloads this page for a world version it does not hold (`reloadForWorldVersion` on the tab's `sessionStorage`),
 * saying in the report why a held reload was held.
 *
 * @param version The version the server named, or null when the page does not know it (a 4007 close).
 * @param why What triggered it, for the diagnostics readout.
 * @returns True when the page is reloading; false when the minute's count is spent or the store is unavailable.
 */
export function reloadPageForWorld(version: string | null, why: string): boolean {
  let store: ReloadStore;
  try {
    store = window.sessionStorage;
  } catch {
    return false;
  }
  const outcome = reloadForWorldVersion(version, store, () => window.location.reload(), Date.now());
  if (outcome.kind === "held") {
    gameReport.closed = `world changed (${why}); reload held, ${WORLD_RELOADS_PER_WINDOW} reloads in the last ${Math.round(outcome.oldestMs / 1000)}s`;
  }
  return outcome.kind === "reloading";
}

/**
 * Takes the version a `welcome` named before the last reload, once: the next load reads it only when `/status` names
 * none.
 *
 * @param store The tab's store.
 * @returns The version, or null when none was kept, it is not a version name, or the store cannot be used.
 */
export function takeVersionHint(store: ReloadStore): string | null {
  try {
    const version = store.getItem(VERSION_KEY);
    store.removeItem(VERSION_KEY);
    return isWorldVersion(version) ? version : null;
  } catch {
    return null;
  }
}

/**
 * The world version the page loads: the one `/status` says the gateway's map server runs, else the one a `welcome`
 * named before the last reload, else null, which reads the pointer as before. Never rejects.
 *
 * @param gatewayOrigin The gateway's HTTP origin, or an empty string when the page has none.
 * @param hint The kept version, or null.
 * @param fetcher The page's `fetch`.
 * @param timeoutMs How long to wait for `/status`.
 * @returns The version and where it came from, or null.
 */
export async function runningWorldVersion(gatewayOrigin: string, hint: string | null, fetcher: typeof fetch = fetch, timeoutMs: number = RUNNING_VERSION_TIMEOUT_MS): Promise<{ readonly version: string; readonly source: "status" | "welcome" } | null> {
  if (gatewayOrigin !== "") {
    const probe = await probeStatus(gatewayOrigin, fetcher, timeoutMs);
    if (probe.kind === "answer" && probe.body.worldVersion !== undefined) {
      return { version: probe.body.worldVersion, source: "status" };
    }
  }
  return hint === null ? null : { version: hint, source: "welcome" };
}
